import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { formatSearchTimestamp } from './adminSearchDetailUtils';

import { ADMIN_BTN_GHOST, ADMIN_CARD, ADMIN_CARD_MUTED, ADMIN_SECTION_TITLE } from './adminTheme';

function StatusPill({ ok, label }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
        ok
          ? 'border-emerald-500/40 bg-emerald-950/40 text-emerald-200'
          : 'border-red-500/40 bg-red-950/40 text-red-200'
      }`}
    >
      {label}
    </span>
  );
}

function outcomeTone(outcome) {
  const o = String(outcome || '').toLowerCase();
  if (o === 'success' || o === 'start') return 'text-emerald-300';
  if (o === 'disabled') return 'text-slate-400';
  if (o === 'timeout') return 'text-amber-300';
  return 'text-red-300';
}

export default function AdminOllamaPanel({ preprocessSearches = [] }) {
  const base = getApiBaseUrl();
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forceSpacy, setForceSpacy] = useState(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState(8);
  const [controlsBusy, setControlsBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [statusRes, eventsRes, controlsRes] = await Promise.all([
        fetch(`${base}/admin/ollama/status`, adminFetchInit()),
        fetch(`${base}/admin/ollama/events?limit=200`, adminFetchInit()),
        fetch(`${base}/admin/ollama/preprocess-controls`, adminFetchInit()),
      ]);
      if (!statusRes.ok) throw new Error(`ollama status HTTP ${statusRes.status}`);
      if (!eventsRes.ok) throw new Error(`ollama events HTTP ${eventsRes.status}`);
      const statusJson = await statusRes.json();
      const eventsJson = await eventsRes.json();
      setStatus(statusJson);
      setEvents(Array.isArray(eventsJson.items) ? eventsJson.items : []);
      if (controlsRes.ok) {
        const controlsJson = await controlsRes.json();
        setForceSpacy(Boolean(controlsJson.force_spacy));
          const t = Number(controlsJson.ollama_timeout_seconds);
          if (Number.isFinite(t)) setTimeoutSeconds(Math.max(3, Math.min(15, t)));
        } else if (statusJson?.preprocess_controls) {
          setForceSpacy(Boolean(statusJson.preprocess_controls.force_spacy));
          const t = Number(statusJson.timeout_seconds);
          if (Number.isFinite(t)) setTimeoutSeconds(Math.max(3, Math.min(15, t)));
        }
    } catch (exc) {
      setError(String(exc?.message || exc));
    } finally {
      setLoading(false);
    }
  }, [base]);

  const persistControls = useCallback(
    async (patch) => {
      setControlsBusy(true);
      setError('');
      try {
        const res = await fetch(
          `${base}/admin/ollama/preprocess-controls`,
          adminFetchInit({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
          }),
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        if (typeof data.force_spacy === 'boolean') setForceSpacy(data.force_spacy);
        const t = Number(data.ollama_timeout_seconds);
        if (Number.isFinite(t)) setTimeoutSeconds(Math.max(1, Math.min(10, t)));
        setStatus((prev) =>
          prev
            ? {
                ...prev,
                timeout_seconds: Number.isFinite(t) ? t : prev.timeout_seconds,
                force_spacy: typeof data.force_spacy === 'boolean' ? data.force_spacy : prev.force_spacy,
                preprocess_controls: data,
              }
            : prev,
        );
      } catch (exc) {
        setError(String(exc?.message || exc));
      } finally {
        setControlsBusy(false);
      }
    },
    [base],
  );

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const stats = useMemo(() => {
    const counts = { success: 0, timeout: 0, unavailable: 0, invalid_json: 0, disabled: 0, other: 0 };
    for (const ev of events) {
      const key = String(ev.outcome || 'other').toLowerCase();
      if (counts[key] != null) counts[key] += 1;
      else counts.other += 1;
    }
    return counts;
  }, [events]);

  return (
    <div className="min-w-0 space-y-4">
      <div className={ADMIN_CARD}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">Ollama runtime</h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-400">
              Local LLM calls for Home preprocess, entity routing, intel refine, and email category enrichment.
              Events are stored in Redis (<span className="font-mono text-cyan-200/90">admin:ollama_events</span>) and
              mirrored to backend logs as <span className="font-mono text-cyan-200/90">[ollama]</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              load();
            }}
            className={ADMIN_BTN_GHOST}
          >
            Refresh
          </button>
        </div>

        {loading && !status ? (
          <p className="mt-4 text-sm text-slate-500">Loading Ollama status…</p>
        ) : null}
        {error ? (
          <p className="mt-4 rounded-lg border border-rose-400/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">{error}</p>
        ) : null}

        {status && !status.reachable && status.enabled ? (
          <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-950/40 px-4 py-3 text-sm leading-relaxed text-amber-100">
            <p className="font-semibold text-amber-50">Ollama is enabled but not reachable from the backend container.</p>
            <p className="mt-2 text-amber-100/90">
              Expected URL: <span className="font-mono text-amber-50">{status.base_url}</span>. With Docker Compose this should be{' '}
              <span className="font-mono text-amber-50">http://ollama:11434</span> — check that the{' '}
              <span className="font-mono text-amber-50">ollama</span> service is up and{' '}
              <span className="font-mono text-amber-50">ollama-model-init</span> finished pulling the model.
            </p>
            <ul className="mt-3 list-inside list-disc space-y-1 text-xs text-amber-100/85">
              <li>
                Dev: <span className="font-mono">docker compose up -d ollama ollama-model-init backend</span>
              </li>
              <li>
                Prod: <span className="font-mono">docker compose -f docker-compose.prod.yml up -d</span> (includes
                Ollama on the internal network).
              </li>
              <li>
                Remove stale override in <span className="font-mono">backend/.env</span> if you still have{' '}
                <span className="font-mono">OLLAMA_BASE_URL=http://host.docker.internal:11434</span>.
              </li>
              <li>
                Disable LLM: <span className="font-mono">OLLAMA_ENABLED=0</span> (spaCy-only fallback).
              </li>
            </ul>
          </div>
        ) : null}

        {status ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className={ADMIN_CARD_MUTED}>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Enabled</p>
              <div className="mt-2">
                <StatusPill ok={status.enabled} label={status.enabled ? 'On' : 'Off'} />
              </div>
            </div>
            <div className={ADMIN_CARD_MUTED}>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Reachable</p>
              <div className="mt-2">
                <StatusPill ok={status.reachable} label={status.reachable ? 'Up' : 'Down'} />
              </div>
            </div>
            <div className={ADMIN_CARD_MUTED}>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Model</p>
              <p className="mt-2 break-all font-mono text-xs text-slate-100">{status.model || '—'}</p>
            </div>
            <div className={ADMIN_CARD_MUTED}>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Timeout</p>
              <p className="mt-2 font-mono text-xs text-slate-100">{status.timeout_seconds ?? '—'}s</p>
            </div>
          </div>
        ) : null}

        <div className={`mt-4 ${ADMIN_CARD_MUTED}`}>
          <h3 className={ADMIN_SECTION_TITLE}>Circuit breaker</h3>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
            Force spaCy skips Ollama on Home Search preprocess (Redis{' '}
            <span className="font-mono text-cyan-200/90">osint:preprocess:force_spacy</span>). Timeout
            threshold is stored in{' '}
            <span className="font-mono text-cyan-200/90">osint:preprocess:ollama_timeout_s</span>.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-6">
            <label className="inline-flex cursor-pointer items-center gap-3">
              <span className="relative inline-flex h-6 w-11 items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={forceSpacy}
                  disabled={controlsBusy}
                  onChange={(e) => persistControls({ force_spacy: e.target.checked })}
                />
                <span className="absolute inset-0 rounded-full bg-slate-600 transition peer-checked:bg-cyan-500 peer-focus:ring-2 peer-focus:ring-cyan-500/40 peer-disabled:opacity-50" />
                <span className="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
              </span>
              <span className="text-xs font-bold text-slate-100">
                Force spaCy Fallback (Bypass Ollama)
              </span>
            </label>
            <label className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-100">
              Timeout Threshold
              <input
                type="number"
                min={3}
                max={15}
                step={0.5}
                value={timeoutSeconds}
                disabled={controlsBusy}
                onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
                onBlur={() => {
                  const clamped = Math.max(3, Math.min(15, Number(timeoutSeconds) || 6));
                  setTimeoutSeconds(clamped);
                  persistControls({ ollama_timeout_seconds: clamped });
                }}
                className="w-20 rounded-lg border border-white/15 bg-slate-950/70 px-2 py-1.5 font-mono text-sm text-slate-100 shadow-sm outline-none ring-cyan-500/30 focus:ring-2 disabled:opacity-50"
              />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                3s – 15s
              </span>
            </label>
          </div>
          {status?.preprocess_timeout_seconds != null || status?.refine_timeout_seconds != null ? (
            <p className="mt-3 text-[11px] text-slate-400">
              Effective budgets — preprocess{' '}
              <span className="font-mono text-slate-200">{status.preprocess_timeout_seconds ?? '—'}s</span>
              , refine{' '}
              <span className="font-mono text-slate-200">{status.refine_timeout_seconds ?? '—'}s</span>
              . Model stays warm via keep-alive; refine runs top-3 after results emit.
            </p>
          ) : null}
          {forceSpacy ? (
            <p className="mt-3 rounded-lg border border-amber-400/40 bg-amber-950/40 px-3 py-2 text-[11px] font-semibold text-amber-100">
              spaCy fallback is forced — Home Search will not wait on Ollama timeouts.
            </p>
          ) : null}
        </div>

        {status ? (
          <dl className="mt-4 grid gap-2 text-xs sm:grid-cols-2">
            <div>
              <dt className="font-bold uppercase tracking-wider text-slate-400">Base URL</dt>
              <dd className="mt-0.5 break-all font-mono text-slate-100">{status.base_url}</dd>
            </div>
            <div>
              <dt className="font-bold uppercase tracking-wider text-slate-400">Version</dt>
              <dd className="mt-0.5 font-mono text-slate-100">{status.version || '—'}</dd>
            </div>
            {status.error ? (
              <div className="sm:col-span-2">
                <dt className="font-bold uppercase tracking-wider text-slate-400">Last error</dt>
                <dd className="mt-0.5 text-rose-300">{status.error}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-4 shadow-sm sm:p-5 lg:col-span-1 min-w-0">
          <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Recent outcomes</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {Object.entries(stats).map(([key, val]) => (
              <li key={key} className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-slate-400">{key}</span>
                <span className="font-bold tabular-nums text-slate-100">{val}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-4 shadow-sm sm:p-5 lg:col-span-2 min-w-0">
          <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Structured events (verbose)</h3>
          <div className="mt-3 max-h-[420px] min-w-0 overflow-auto rounded-lg border border-slate-100">
            <table className="w-full min-w-[40rem] border-collapse text-left text-xs">
              <thead className="sticky top-0 bg-slate-950">
                <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Context</th>
                  <th className="px-3 py-2">Outcome</th>
                  <th className="px-3 py-2">Latency</th>
                  <th className="px-3 py-2">Model / budget</th>
                  <th className="px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const extra = ev.extra && typeof ev.extra === 'object' ? ev.extra : {};
                  const budget =
                    extra.read_timeout_s != null
                      ? `${extra.read_timeout_s}s read · ${extra.num_predict ?? '—'} tok`
                      : '—';
                  return (
                    <tr key={ev.id || `${ev.ts}-${ev.context}-${ev.outcome}`} className="border-b border-slate-100 align-top">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-slate-500">{ev.ts}</td>
                      <td className="px-3 py-2 font-semibold text-slate-200">{ev.context}</td>
                      <td className={`px-3 py-2 font-bold uppercase ${outcomeTone(ev.outcome)}`}>{ev.outcome}</td>
                      <td className="px-3 py-2 font-mono text-slate-300">
                        {ev.latency_ms != null ? `${ev.latency_ms} ms` : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-400">
                        <div>{ev.model || '—'}</div>
                        <div>{budget}</div>
                      </td>
                      <td className="max-w-[280px] px-3 py-2 font-mono text-[10px] leading-snug text-slate-400">
                        {ev.error ? <div className="text-rose-300">{ev.error}</div> : null}
                        {extra.prompt_chars != null ? <div>prompt_chars={extra.prompt_chars}</div> : null}
                        {extra.response_chars != null ? <div>response_chars={extra.response_chars}</div> : null}
                        {extra.http_status != null ? <div>http={extra.http_status}</div> : null}
                        {extra.eval_count != null ? <div>eval_count={extra.eval_count}</div> : null}
                        {!ev.error && extra.prompt_chars == null && extra.http_status == null ? '—' : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {events.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No Ollama events recorded yet.</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-4 shadow-sm sm:p-5 min-w-0">
        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Recent preprocess calls</h3>
        <p className="mt-1 text-xs text-slate-500">
          From <span className="font-mono">admin:last_searches</span> — inspect Ollama vs spaCy fallback per query.
        </p>

        {/* Mobile: stacked cards (table overflows on narrow viewports) */}
        <ul className="mt-3 max-h-[280px] space-y-2 overflow-y-auto sm:hidden" data-testid="ollama-preprocess-mobile">
          {preprocessSearches.length === 0 ? (
            <li className="py-8 text-center text-sm text-slate-400">No preprocess requests recorded yet.</li>
          ) : (
            preprocessSearches.map((it) => {
              const body = it.result_json || {};
              return (
                <li
                  key={it.id || it.ts}
                  className="rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-slate-500">
                      {formatSearchTimestamp(it.ts)}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        body.source === 'ollama'
                          ? 'bg-violet-100 text-violet-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {body.source || '—'}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-300">
                    <span>
                      Intent:{' '}
                      <span className="font-semibold text-slate-100">
                        {body.scan_intent || body.scanIntent || '—'}
                      </span>
                    </span>
                    <span>
                      Cached:{' '}
                      <span className="font-semibold text-slate-100">{body.cached ? 'yes' : 'no'}</span>
                    </span>
                  </div>
                  {it.query ? (
                    <p className="mt-1 truncate font-mono text-[10px] text-slate-500" title={String(it.query)}>
                      {String(it.query)}
                    </p>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>

        {/* sm+: table with horizontal scroll */}
        <div className="mt-3 hidden max-h-[280px] min-w-0 overflow-auto rounded-lg border border-slate-100 sm:block">
          <table className="w-full min-w-[28rem] border-collapse text-left text-xs">
            <thead className="sticky top-0 bg-slate-950">
              <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Intent</th>
                <th className="px-3 py-2">Cached</th>
              </tr>
            </thead>
            <tbody>
              {preprocessSearches.map((it) => {
                const body = it.result_json || {};
                return (
                  <tr key={it.id || it.ts} className="border-b border-slate-100">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-slate-500">
                      {formatSearchTimestamp(it.ts)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          body.source === 'ollama'
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {body.source || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{body.scan_intent || body.scanIntent || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{body.cached ? 'yes' : 'no'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {preprocessSearches.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No preprocess requests recorded yet.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
